'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ShoppingBag, Star, ChevronRight } from 'lucide-react'
import { discardHTMLTags, extractPromoDiscountTag, getBaseImageUrl, getCurrencySymbol, getMerchantHref } from '@/constants/hooks'
import { Merchant, OffersOffer } from '@/services/dataTypes'
import OfferOutUrl from '@/components/shared/OfferOutUrl'
import OfferDetailsToggle from './OfferDetailsToggle'
import DiscountTag from './DiscountTag'
import ExpiryTag from './ExpiryTag'

interface Props {
    offer: OffersOffer
    mer_slug_type: string
    mer_slug: string
    type?: string
    merchant: Merchant
    productDetailUrl?: string | null
    domain?: string
}

function isValidImageUrl(src?: string | null): boolean {
    if (!src || typeof src !== 'string') return false
    const s = src.trim()
    if (!s || s === 'null' || s === 'undefined' || s === '/null' || s === 'none') return false
    if (s.includes('awltovhc.com') || s.includes('emjcd.com') || s.includes('anrdoezrs.net') || s.includes('linksynergy.com') || s.includes('clickserve.')) {
        return false
    }
    return true
}

const ProductCard = ({ offer, mer_slug_type, mer_slug, type, merchant, productDetailUrl, domain = '' }: Props) => {
    const merchantHref = getMerchantHref(merchant, mer_slug, mer_slug_type)
    const product = offer?.offer || offer

    const rawProductImg =
        (isValidImageUrl(product?.product_image) ? product?.product_image : null) ||
        (isValidImageUrl((product as any)?.offer?.product_image) ? (product as any)?.offer?.product_image : null) ||
        (isValidImageUrl((product as any)?.image) ? (product as any)?.image : null) ||
        ''

    const rawMerchantLogo =
        (isValidImageUrl(merchant?.merchant_logo) ? merchant?.merchant_logo : null) ||
        (isValidImageUrl((offer as any)?.merchant?.merchant_logo) ? (offer as any)?.merchant?.merchant_logo : null) ||
        ''

    const effectiveDomain = (!domain || domain === 'no domain in cookie' || domain.startsWith('localhost')) ? '' : domain

    const productImgUrl = rawProductImg ? getBaseImageUrl(effectiveDomain, rawProductImg, '') : ''
    const logoImgUrl = rawMerchantLogo ? getBaseImageUrl(effectiveDomain, rawMerchantLogo, '') : ''

    const initialSrc = productImgUrl || logoImgUrl
    const [currentImg, setCurrentImg] = useState<string>(initialSrc)

    const handleImgError = () => {
        if (currentImg === productImgUrl && logoImgUrl && logoImgUrl !== productImgUrl) {
            setCurrentImg(logoImgUrl)
        }
    }

    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
    const discountPercent = originalPrice > 0 && salePrice > 0 ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const finalDiscountTag = extractPromoDiscountTag(product?.offer_title || product?.offer_detail) || (discountPercent ? `${discountPercent}% Off` : null)
    const currencySymbol = getCurrencySymbol(product?.currency)
    const displayName = merchant?.merchant_name || 'Store'
    const displayTitle = product?.offer_title ? discardHTMLTags(product.offer_title) : displayName

    return (
        <div className="group relative bg-white rounded-2xl border border-slate-300 hover:border-[#00d69f]/40 hover:shadow-[0_12px_30px_rgba(0,214,159,0.08)] transition-all duration-300 hover:-translate-y-1 flex flex-col h-full justify-between overflow-hidden w-full shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
            {finalDiscountTag && <DiscountTag tag={finalDiscountTag} />}

            {/* Product Image Area: product image if available, falls back to store logo (same as Theme-5) */}
            <div className="relative bg-slate-50/60 p-3 h-40 flex items-center justify-center overflow-hidden border-b border-slate-200 group-hover:bg-[#f8fafc]/50 transition-colors duration-300">
                {currentImg ? (
                    <Image 
                        src={currentImg} 
                        alt={displayTitle} 
                        width={200} 
                        height={200}
                        unoptimized
                        onError={handleImgError}
                        className="object-contain w-full h-full max-h-30 p-1 transition-transform duration-500 group-hover:scale-105" 
                        loading="lazy" 
                    />
                ) : null}
            </div>

            {/* Product Details */}
            <div className="flex flex-col flex-1 p-3 gap-1.5">
                <Link href={merchantHref} className="w-fit">
                    <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 bg-[#00d69f]" />
                        <span className="text-[10px] font-bold text-[#00a87a] uppercase tracking-wider">
                            {displayName}
                        </span>
                    </div>
                </Link>

                <div className="min-h-[2.5rem] flex items-center">
                    {product?.is_detail === 1 && productDetailUrl ? (
                        <Link href={productDetailUrl}>
                            <h3 className="text-sm font-bold text-slate-900 line-clamp-2 leading-snug hover:text-[#00a87a] transition-colors">
                                {discardHTMLTags(product?.offer_title)}
                            </h3>
                        </Link>
                    ) : (
                        <h3 className="text-sm font-bold text-slate-900 line-clamp-2 leading-snug group-hover:text-[#00a87a] transition-colors">
                            {discardHTMLTags(product?.offer_title)}
                        </h3>
                    )}
                </div>

                {discardHTMLTags(product?.offer_detail) && (
                    <p className="text-xs text-slate-800 line-clamp-2 leading-relaxed min-h-[2rem] font-normal">
                        {discardHTMLTags(product?.offer_detail)}
                    </p>
                )}

                <div className="flex items-center gap-1">
                    <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map(s => (
                            <Star key={s} size={10} className={`${s <= 4 ? 'text-amber-400 fill-amber-400' : 'text-amber-300 fill-amber-300/40'}`} />
                        ))}
                    </div>
                    <span className="text-[9px] text-slate-400 font-semibold">(2.3k)</span>
                </div>

                {/* Pricing row */}
                {originalPrice > 0 && salePrice > 0 && (
                    <div className="flex items-baseline gap-2 flex-wrap pt-0.5">
                        <span className="text-base font-extrabold text-slate-900">
                            {currencySymbol}{salePrice.toFixed(2)}
                        </span>
                        <span className="text-xs text-slate-400 line-through font-medium">
                            {currencySymbol}{originalPrice.toFixed(2)}
                        </span>
                    </div>
                )}

                {/* Expiry & Toggle */}
                <div className="flex items-center justify-between gap-2 mt-auto pt-1.5 border-t border-slate-200">
                    <ExpiryTag endDate={product?.end_date} />
                    <OfferDetailsToggle
                        domain={domain}
                        imageSrc={currentImg || logoImgUrl}
                        merchantImg={rawMerchantLogo}
                        merchantHref={merchantHref}
                        offer={product}
                        type="anchor"
                        modalType="product"
                        merchantName={displayName}
                    />
                </div>
            </div>

            {/* CTA Button */}
            <div className="px-3 pb-3">
                <OfferOutUrl 
                    unique_id={product?.unique_id} 
                    outUrl={product?.url} 
                    merchantHref={merchantHref} 
                    domain={domain}
                    customClass="w-full flex items-center justify-center gap-1.5 bg-[#071822] hover:bg-[#00d69f] hover:text-[#071822] text-white font-bold text-[11px] uppercase tracking-wider py-2 rounded-xl transition-all duration-300 shadow-xs hover:shadow-md active:scale-98 cursor-pointer"
                >
                    <ShoppingBag size={13} />
                    <span>{product?.coupon_code ? 'Show Code' : (type === 'product' ? 'Buy Now' : 'Get Deal')}</span>
                    <ChevronRight size={13} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                </OfferOutUrl>
            </div>
        </div>
    )
}

export default ProductCard
